import "./ClinicalFormDetailsView.css";
import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { MemberAnswerInfo, QuestionInfo } from "clinical-forms-experience-client";
import { Datepicker, MbscDatepickerChangeEvent } from "@mobiscroll/react";
import { DatepickerBase } from "@mobiscroll/react/dist/src/core/components/datepicker/datepicker";
import ReactQuill from "react-quill";
import { Accordion } from "react-bootstrap";

interface Props {
  index: number;
  parentIndex?: number;
  question: QuestionInfo;
  savedAnswers?: Map<string, MemberAnswerInfo>;
  onAnswerChange: (questionId: string, answer: MemberAnswerInfo) => void;
  readOnly: boolean;

  onMount?: (id: string) => void;
  onUnmount?: (id: string) => void;
}

const ClinicalFormQuestionView: React.FC<Props> = ({
  index,
  parentIndex = -1,
  question,
  savedAnswers,
  onAnswerChange,
  readOnly,
  onMount,
  onUnmount
}: Props) => {
  const [optionsSelected, setOptionsSelected] = React.useState<Set<string>>(
    new Set()
  );

  const [notes, setNotes] = React.useState<string>();
  const [openPicker, setOpenPicker] = useState<boolean>(false);

  useEffect(() => onMount?.(question.id), [question]);
  useEffect(() => () => onUnmount?.(question.id), []);

  useEffect(() => {
    const savedAnswer = savedAnswers?.get(question.id);
    if (!savedAnswer) {
      return;
    }
    setOptionsSelected(new Set(parseOptions(savedAnswer?.answer, question)));
    setNotes(savedAnswer?.notes);
  }, [savedAnswers]);

  const onChoicesAnswersChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      if (question.type === "single-choice") {
        optionsSelected.clear();
      }
      optionsSelected.add(event.target.value);
    } else {
      optionsSelected.delete(event.target.value);
    }

    setOptionsSelected(new Set(optionsSelected));
    const answersString = Array.from(optionsSelected).join(",");
    onAnswerChange(question.id, {questionId: question.id, answer: answersString, notes: notes});
  };

  // radio button deselection
  const onChoiceClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (
      question.type === "single-choice" &&
      !question.isRequired &&
      optionsSelected.has(event.currentTarget.value)
    ) {
      optionsSelected.delete(event.currentTarget.value);
      setOptionsSelected(new Set(optionsSelected));
      onAnswerChange(question.id, {questionId: question.id, answer: "", notes: notes});
    }
  };

  const onTextAnswerChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    optionsSelected.clear();
    optionsSelected.add(event.target.value);
    setOptionsSelected(new Set(optionsSelected));
    onAnswerChange(question.id, {questionId: question.id, answer: event.target.value, notes: notes});
  };

  const onNumberAnswerChange = (event: ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;

    if (question.type === "number-int") {
      value = value.replace(/[^0-9]/g, "");
    } else if (question.type === "number-float") {
      value = value
        .replace(/[^0-9.]/g, "")
        .replace(/(\..*?)\..*/g, "$1")
        .replace(/^(\d+\.?\d{0,2}).*/, "$1");
    } else {
      value = value.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1");
    }

    optionsSelected.clear();
    optionsSelected.add(value);
    setOptionsSelected(new Set(optionsSelected));

    onAnswerChange(question.id, {
      questionId: question.id,
      answer: value,
      notes: notes,
    });
  };

  const onNumberInput = (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.currentTarget;
    let value = target.value;

    if (question.type === "number-int") {
      value = value.replace(/[^0-9]/g, "");
    } else if (question.type === "number-float") {
      value = value
        .replace(/[^0-9.]/g, "")
        .replace(/(\..*?)\..*/g, "$1")
        .replace(/^(\d+\.?\d{0,2}).*/, "$1");
    } else {
      value = value.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1");
    }

    target.value = value;
    setOptionsSelected(new Set([value]));
  };

  const onDateAnswerChange = (event: MbscDatepickerChangeEvent, inst: DatepickerBase) => {
    optionsSelected.clear();
    optionsSelected.add(event.valueText ?? "");
    setOptionsSelected(new Set(optionsSelected));
    onAnswerChange(question.id, {questionId: question.id, answer: event.valueText ?? "", notes: notes});
  };

  const onNotesChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(event.target.value);
    const answersString = Array.from(optionsSelected).join(",");
    onAnswerChange(question.id, {questionId: question.id, answer: answersString, notes: event.target.value});
  }

  return (
    <>
      <div
        key={question.id}
        className={`cn-block ${question.type === 'description' ? 'cn-block--section' : question.type === 'collapsible' ? 'cn-block--script' : 'cn-block--question'} ${parentIndex >= 0 ? 'cn-block--sub' : ''} ${question.isRequired ? 'cn-block--required' : ''} ${question.importance ? 'cn-importance--' + question.importance : ''} d-flex px-4 py-3 mt-3 ${['description', 'collapsible'].includes(question.type) ? 'bg-white border-warning border-start border-orange border-3' : 'bg-gray-FAF border rounded-3'}`}
      >
        {!['description', 'collapsible'].includes(question.type) && <div className="pe-2 cn-num">
          {parentIndex >= 0 && <span>{parentIndex + 1}.</span>}
          {index + 1}.
        </div>}
        <div className="flex-fill">
          {question.type !== "collapsible" && (
            <strong>
              <pre style={{ font: "unset", whiteSpace: "break-spaces" }}>
                {question.questionText}
                {question.isRequired && (
                  <>
                    <span
                      className="cn-required-asterisk"
                      title="Required"
                      aria-label="Required"
                    >
                      *
                    </span>
                    <span
                      className="cn-required-badge"
                      title="Required"
                      aria-label="Required"
                    >
                      Required
                    </span>
                  </>
                )}
              </pre>
            </strong>
          )}
          {(question.type === "single-choice" ||
            question.type === "multiple-choice") && (
            <div className="row">
              {question.options?.map((option) => (
                <div
                  key={option.id}
                  className={"mt-2 " + (question.optionWidthOnUi ?? "col-sm-6")}
                >
                  <div key={option.id} className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type={
                        question.type === "single-choice" ? "radio" : "checkbox"
                      }
                      name={`question-${question.id}`}
                      id={`option-${option.id}`}
                      value={option.id}
                      onChange={onChoicesAnswersChange}
                      onClick={onChoiceClick}
                      checked={optionsSelected.has(option.id)}
                      disabled={readOnly}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`option-${option.id}`}
                      dangerouslySetInnerHTML={{ __html: option.optionText ?? "" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          {question.type === "text" && (
            <div key={question.id} className="mt-2">
              <div className="form-field">
                <textarea
                  className="form-input w-100 form-control form-control-sm"
                  name={`question-${question.id}`}
                  placeholder="Type your answer here"
                  value={
                    optionsSelected.size > 0
                      ? Array.from(optionsSelected)[0]
                      : ""
                  }
                  onChange={onTextAnswerChange}
                  disabled={readOnly}
                  required={question.isRequired}
                />
              </div>
            </div>
          )}
          {question.type === "description" && (
            <div key={question.id} className="mt-2">
                  {question.options?.map((option) =>
                    <div key={option.id} className="fs-14 p-2 rounded-2 bg-white border care-note-quill" >
                        <ReactQuill
                          value={option.optionText}
                          readOnly={true}
                          theme="bubble"
                          className="fs-14"
                          modules={{ toolbar: false }}
                        />
                    </div>
                  )}
            </div>
          )}
          {question.type === "collapsible" && (
            <div key={question.id}>
                  {question.options?.map((option) =>
                    <div key={option.id} className="p-0">
                    <Accordion defaultActiveKey="0">
                      <Accordion.Item eventKey="0">
                          <Accordion.Header>{question.questionText}</Accordion.Header>
                          <Accordion.Body>
                            <div className="care-note-quill">
                              <ReactQuill
                                value={option.optionText}
                                readOnly={true}
                                theme="bubble"
                                modules={{ toolbar: false }}
                              />
                            </div>
                          </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                      {/* {option.optionText} */}

                    </div>
                  )}
            </div>
          )}
          {question.type.startsWith("number") && (
            <div key={question.id} className="mt-2">
              <div className="form-field">
                <input
                  type="text"
                  className="form-input"
                  name={`question-${question.id}`}
                  placeholder="Enter number here"
                  value={
                    optionsSelected.size > 0
                      ? Array.from(optionsSelected)[0]
                      : ""
                  }
                  onChange={onNumberAnswerChange}
                  onInput={onNumberInput}
                  disabled={readOnly}
                />
              </div>
            </div>
          )}
          {question.type.startsWith("date") && (
            <div key={question.id} className="mt-2" style={{ maxWidth: "200px" }}>
              <div className="mobiscroll">
                <div className="mobiscroll__calendar">
                  <Datepicker
                    id="dueOn"
                    cssClass="w-auto"
                    value={
                      optionsSelected.size > 0
                        ? Array.from(optionsSelected)[0]
                        : ""
                    }
                    controls={["calendar"]}
                    calendarType="month"
                    theme="windows"
                    themeVariant="light"
                    placeholder="MM/DD/YYYY"
                    display="bubble"
                    touchUi={true}
                    showOnClick={false}
                    isOpen={openPicker}
                    onClose={() => {setOpenPicker(false)}}
                    onChange={onDateAnswerChange}
                    buttons={[]}
                    required={question.isRequired}
                    disabled={readOnly}
                  />
                </div>
                    <div onClick={() => setOpenPicker(!openPicker)} className="mobiscroll__calendar-icon">
                        <span className="material-symbols-outlined fs-18" >
                          calendar_month
                        </span>
                    </div>
              </div>
            </div>
          )}
          {question.enableNotes && (
            <div className="my-3">
              <textarea
                className="form-field form-field-sm w-100"
                name="CareNoteType"
                placeholder="Notes"
                value={notes}
                onChange={onNotesChange}
                disabled={readOnly}
                rows={2}
              ></textarea>
            </div>
          )}
          {(question.type === "single-choice" ||
            question.type === "multiple-choice") &&
            question.options?.map(
              (option) =>
                optionsSelected.has(option.id) &&
                option.dependentQuestions?.map(
                  (dependentQuestion, childIndex) => (
                    <ClinicalFormQuestionView
                      key={dependentQuestion.id}
                      index={childIndex}
                      parentIndex={index}
                      question={dependentQuestion}
                      onAnswerChange={onAnswerChange}
                      savedAnswers={savedAnswers}
                      readOnly={readOnly}
                      onMount={onMount}
                      onUnmount={onUnmount}
                    />
                  )
                )
            )}
        </div>
      </div>
    </>
  );
};

export default ClinicalFormQuestionView;

function parseOptions(
  savedAnswer: string | undefined,
  question: QuestionInfo
): string[] {
  if (!savedAnswer || savedAnswer === "") {
    return [];
  }
  return question.type === "text" ? [savedAnswer] : savedAnswer.split(",");
}
